import { auth } from '@/config/firebase';
import { CENTRAL_API_URL } from '@/config/env';

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

// Types

export interface Release {
  id: string;
  version: string;
  artifactGcsPath: string;
  checksum: string;
  releaseNotes: string;
  requirementsChanged: boolean;
  size: number;
  status: 'draft' | 'published' | 'deprecated';
  createdAt: any;
  createdBy: string;
}

export interface DeploymentProgress {
  total: number;
  succeeded: number;
  failed: number;
  in_progress: number;
  pending: number;
}

export interface DeviceDeploymentStatus {
  status: 'pending' | 'downloading' | 'verifying' | 'applying' | 'restarting' | 'healthy' | 'failed' | 'rolled_back';
  startedAt?: string;
  completedAt?: string;
  error?: string;
  progress?: number;
  previousVersion?: string;
}

export interface Deployment {
  id: string;
  releaseId: string;
  releaseVersion: string;
  targets: {
    deviceIds?: string[];
    locationId?: string;
    clusterId?: string;
    all?: boolean;
  };
  strategy: 'all_at_once' | 'rolling' | 'canary';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  progress: DeploymentProgress;
  deviceStatuses: Record<string, DeviceDeploymentStatus>;
  createdAt: any;
  createdBy: string;
  completedAt?: any;
}

// API methods

export const deploymentService = {
  async getReleases(): Promise<Release[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments/releases`, { headers });
    if (!res.ok) throw new Error(`Failed to fetch releases: ${res.statusText}`);
    const data = await res.json();
    return data.releases || [];
  },

  async createRelease(release: Omit<Release, 'id' | 'createdAt' | 'createdBy' | 'status'>): Promise<Release> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments/releases`, {
      method: 'POST',
      headers,
      body: JSON.stringify(release),
    });
    if (!res.ok) throw new Error(`Failed to create release: ${res.statusText}`);
    return res.json();
  },

  async deploy(params: {
    releaseId: string;
    targets: { deviceIds?: string[]; locationId?: string; clusterId?: string; all?: boolean };
    strategy?: 'all_at_once' | 'rolling' | 'canary';
  }): Promise<{ deploymentId: string; version: string; totalDevices: number; notified: number }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments/deploy`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });
    if (!res.ok) throw new Error(`Failed to deploy: ${res.statusText}`);
    return res.json();
  },

  async getDeployments(): Promise<Deployment[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments`, { headers });
    if (!res.ok) throw new Error(`Failed to fetch deployments: ${res.statusText}`);
    const data = await res.json();
    return data.deployments || [];
  },

  async getDeployment(id: string): Promise<Deployment> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments/${id}`, { headers });
    if (!res.ok) throw new Error(`Failed to fetch deployment: ${res.statusText}`);
    return res.json();
  },

  async rollback(deploymentId: string, reason?: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/deployments/${deploymentId}/rollback`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: reason || 'Admin-initiated rollback' }),
    });
    if (!res.ok) throw new Error(`Failed to rollback: ${res.statusText}`);
  },

  async approveDevice(deviceId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/devices/${deviceId}/approve`, {
      method: 'POST',
      headers,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(body.error || `Failed to approve device: ${res.statusText}`);
    }
  },

  async rejectDevice(deviceId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${CENTRAL_API_URL}/devices/${deviceId}/reject`, {
      method: 'POST',
      headers,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(body.error || `Failed to reject device: ${res.statusText}`);
    }
  },
};
